"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Post {
  slug: string;
  pathPrefix?: string;
  metadata: {
    title: string;
    description: string;
    tags: string[];
    publishedAt: string;
    lastModifiedAt?: string;
  };
}

interface RelatedTagPostsProps {
  tags: string[];
  posts: Post[];
  currentSlug: string;
  pathPrefix: string;
  expandedGroupTags?: string[];
  hideExamColumn?: boolean;
}

const INITIAL_DISPLAY_COUNT = 3;

export default function RelatedTagPosts({
  tags,
  posts,
  currentSlug,
  pathPrefix,
  expandedGroupTags,
  hideExamColumn = false,
}: RelatedTagPostsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // 현재 글과 같은 태그를 가진 관련 글들을 찾기
  const relatedPosts = useMemo(() => {
    return posts.filter((post) => {
      if (post.slug === currentSlug) return false;
      return post.metadata.tags.some((tag) => tags.includes(tag));
    });
  }, [posts, currentSlug, tags]);

  // 초기 3개 표시용: 가장 관련성 높은 글들 (더 많은 태그가 겹치는 글 우선)
  const initialPosts = useMemo(() => {
    const postsWithScore = relatedPosts.map((post) => {
      const matchingTags = post.metadata.tags.filter((tag) =>
        tags.includes(tag)
      );
      // 세부 태그(뒤쪽 태그)에 더 높은 점수 부여
      const score = matchingTags.reduce((acc, tag) => {
        const tagIndex = tags.indexOf(tag);
        return acc + (tags.length - tagIndex);
      }, 0);
      return { post, score, matchingTags };
    });

    return postsWithScore
      .sort((a, b) => b.score - a.score)
      .slice(0, INITIAL_DISPLAY_COUNT)
      .map((item) => item.post);
  }, [relatedPosts, tags]);

  // 더보기용: 태그별로 그룹화
  const groupedByTag = useMemo(() => {
    // expandedGroupTags가 있으면 해당 태그로 그룹화 (연도별 등)
    // 없으면 기존 동작: 현재 글 태그 역순으로 그룹화
    const groupTags = expandedGroupTags ?? [...tags].reverse();
    const displayedSlugs = new Set<string>();
    const groups: { tag: string; posts: Post[] }[] = [];

    for (const tag of groupTags) {
      const postsForTag = relatedPosts.filter((post) => {
        // 이미 표시된 글은 제외
        if (displayedSlugs.has(post.slug)) return false;
        // 해당 태그를 가진 글만
        return post.metadata.tags.includes(tag);
      });

      if (postsForTag.length > 0) {
        // 표시된 글로 마킹
        postsForTag.forEach((post) => displayedSlugs.add(post.slug));
        groups.push({ tag, posts: postsForTag });
      }
    }

    return groups;
  }, [relatedPosts, tags, expandedGroupTags]);

  // 관련 글이 없으면 렌더링하지 않음
  if (relatedPosts.length === 0) return null;

  const hasMorePosts = relatedPosts.length > INITIAL_DISPLAY_COUNT;

  return (
    <div className="mt-8 mx-2 sm:mx-0">
      <div className="flex flex-col rounded-md border-[0.5px] p-3 sm:p-4 dark:border-neutral-400">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-[#333] dark:text-neutral-200">
              관련 글
            </h3>
            <span className="text-xs text-[#706E6E] dark:text-neutral-400">
              ({relatedPosts.length}개)
            </span>
          </div>
          {hasMorePosts && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors flex items-center gap-1"
            >
              {isExpanded ? (
                <>
                  접기
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </>
              ) : (
                <>
                  더보기
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </>
              )}
            </button>
          )}
        </div>

        {/* 접힘 상태: 3개만 테이블로 표시.
            SEO(JCG-2502): 펼침 블록과 함께 항상 DOM에 렌더하고 CSS(hidden)로만 토글해
            전체 관련글 <Link>가 초기 SSR HTML에 포함되도록 한다. */}
        <div className={cn(isExpanded && "hidden")}>
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-600">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow className="bg-amber-100 dark:bg-amber-900 border-0">
                  <TableHead className="text-left text-sm font-semibold pl-2">
                    제목
                  </TableHead>
                  <TableHead className="text-center text-sm font-semibold w-[90px] sm:w-[180px]">
                    태그
                  </TableHead>
                  <TableHead className="hidden sm:table-cell text-center text-sm font-semibold w-[90px]">
                    업데이트
                  </TableHead>
                  {!hideExamColumn && (
                    <TableHead className="text-center text-sm font-semibold w-[45px] sm:w-[50px]">
                      시험
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {initialPosts.map((post) => {
                  const isExamPost = post.slug.includes("gamja-exam");
                  return (
                    <TableRow
                      key={post.slug}
                      className={
                        !hideExamColumn && isExamPost
                          ? "bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900"
                          : ""
                      }
                    >
                      <TableCell className="pl-2">
                        <Link
                          href={`/${post.pathPrefix ?? pathPrefix}/${post.slug}`}
                          prefetch={false}
                          className="text-neutral-900 dark:text-neutral-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm block truncate"
                          title={post.metadata.title}
                        >
                          {post.metadata.title}
                        </Link>
                      </TableCell>
                      <TableCell className="overflow-hidden">
                        <div className="flex gap-1 overflow-hidden">
                          {/* 모바일: 가장 세부 태그 1개 (마지막 태그) */}
                          <span className="sm:hidden flex gap-1 items-center">
                            <span className="text-xs px-1 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300 whitespace-nowrap">
                              {post.metadata.tags[post.metadata.tags.length - 1]}
                            </span>
                            {post.metadata.tags.length > 1 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
                                    +{post.metadata.tags.length - 1}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2">
                                  <div className="flex flex-wrap gap-1">
                                    {post.metadata.tags.slice(0, -1).map((tag) => (
                                      <span
                                        key={tag}
                                        className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </span>
                          {/* 데스크탑: 3개 */}
                          <span className="hidden sm:flex gap-1 items-center">
                            {post.metadata.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="text-xs px-1 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300 whitespace-nowrap"
                              >
                                {tag}
                              </span>
                            ))}
                            {post.metadata.tags.length > 3 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
                                    +{post.metadata.tags.length - 3}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2">
                                  <div className="flex flex-wrap gap-1">
                                    {post.metadata.tags.slice(3).map((tag) => (
                                      <span
                                        key={tag}
                                        className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-center text-xs text-neutral-600 dark:text-neutral-400 truncate">
                        {post.metadata.lastModifiedAt || post.metadata.publishedAt}
                      </TableCell>
                      {!hideExamColumn && (
                        <TableCell className="text-center">
                          {isExamPost ? (
                            <Link
                              href={`/${post.pathPrefix ?? pathPrefix}/${post.slug}`}
                              prefetch={false}
                              className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded transition-colors"
                            >
                              응시
                            </Link>
                          ) : (
                            <span className="text-xs text-neutral-400">-</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* 펼침 상태: 태그별로 그룹화해서 표시.
            항상 DOM에 렌더하고 CSS(hidden)로만 토글 — 전체 관련글 <Link>가 초기 SSR HTML에 포함됨. */}
        <div className={cn("flex flex-col gap-4", !isExpanded && "hidden")}>
            {groupedByTag.map(({ tag, posts: tagPosts }) => (
              <div key={tag}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900 rounded-md text-[#706E6E] dark:text-neutral-200 font-medium">
                    {tag}
                  </span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {tagPosts.length}개
                  </span>
                </div>
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-600">
                  <Table className="w-full table-fixed">
                    <TableHeader>
                      <TableRow className="bg-gray-50 dark:bg-neutral-800 border-0">
                        <TableHead className="text-left text-sm font-medium pl-2">
                          제목
                        </TableHead>
                        <TableHead className="text-center text-sm font-medium w-[90px] sm:w-[180px]">
                          태그
                        </TableHead>
                        <TableHead className="hidden sm:table-cell text-center text-sm font-medium w-[90px]">
                          업데이트
                        </TableHead>
                        {!hideExamColumn && (
                          <TableHead className="text-center text-sm font-medium w-[45px] sm:w-[50px]">
                            시험
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tagPosts.map((post) => {
                        const isExamPost = post.slug.includes("gamja-exam");
                        return (
                          <TableRow
                            key={post.slug}
                            className={
                              !hideExamColumn && isExamPost
                                ? "bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900"
                                : ""
                            }
                          >
                            <TableCell className="pl-2">
                              <Link
                                href={`/${post.pathPrefix ?? pathPrefix}/${post.slug}`}
                                prefetch={false}
                                className="text-neutral-900 dark:text-neutral-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm block truncate"
                                title={post.metadata.title}
                              >
                                {post.metadata.title}
                              </Link>
                            </TableCell>
                            <TableCell className="overflow-hidden">
                              <div className="flex gap-1 overflow-hidden">
                                {/* 모바일: 가장 세부 태그 1개 (마지막 태그) */}
                                <span className="sm:hidden flex gap-1 items-center">
                                  <span className="text-xs px-1 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300 whitespace-nowrap">
                                    {post.metadata.tags[post.metadata.tags.length - 1]}
                                  </span>
                                  {post.metadata.tags.length > 1 && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
                                          +{post.metadata.tags.length - 1}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-2">
                                        <div className="flex flex-wrap gap-1">
                                          {post.metadata.tags.slice(0, -1).map((postTag) => (
                                            <span
                                              key={postTag}
                                              className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300"
                                            >
                                              {postTag}
                                            </span>
                                          ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </span>
                                {/* 데스크탑: 3개 */}
                                <span className="hidden sm:flex gap-1 items-center">
                                  {post.metadata.tags.slice(0, 3).map((postTag) => (
                                    <span
                                      key={postTag}
                                      className="text-xs px-1 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300 whitespace-nowrap"
                                    >
                                      {postTag}
                                    </span>
                                  ))}
                                  {post.metadata.tags.length > 3 && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
                                          +{post.metadata.tags.length - 3}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-2">
                                        <div className="flex flex-wrap gap-1">
                                          {post.metadata.tags.slice(3).map((postTag) => (
                                            <span
                                              key={postTag}
                                              className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-neutral-700 rounded text-[#706E6E] dark:text-neutral-300"
                                            >
                                              {postTag}
                                            </span>
                                          ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-center text-xs text-neutral-600 dark:text-neutral-400 truncate">
                              {post.metadata.lastModifiedAt || post.metadata.publishedAt}
                            </TableCell>
                            {!hideExamColumn && (
                              <TableCell className="text-center">
                                {isExamPost ? (
                                  <Link
                                    href={`/${post.pathPrefix ?? pathPrefix}/${post.slug}`}
                                    prefetch={false}
                                    className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded transition-colors"
                                  >
                                    응시
                                  </Link>
                                ) : (
                                  <span className="text-xs text-neutral-400">-</span>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
